import apiClient from '../../shared/services/apiClient';

/**
 * SSL Service (User Version)
 *
 * Handles user-level SSL certificate operations.
 * Users can manage their own SSL certificates and orders.
 *
 * All routes use /api/user/ssl/* prefix
 */
class SSLService {
  // ==========================================
  // Certificate Management
  // ==========================================

  /**
   * Get user SSL certificates
   * @param {number} page - Page number
   * @param {number} perPage - Items per page
   */
  async getCertificates(page = 1, perPage = 10) {
    try {
      const response = await apiClient.get('/api/user/ssl/certificates', {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single SSL certificate
   * @param {number} id - Certificate ID
   */
  async getCertificate(id) {
    try {
      const response = await apiClient.get('/api/user/ssl/certificates/get', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Upload SSL certificate
   * @param {object} data - Certificate data (cert, key, chain, etc.)
   */
  async uploadCertificate(data) {
    try {
      const response = await apiClient.post('/api/user/ssl/upload', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete SSL certificate
   * @param {number} id - Certificate ID
   */
  async deleteCertificate(id) {
    try {
      const response = await apiClient.delete('/api/user/ssl/certificates/delete', {
        data: { id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Toggle auto-renew for a certificate
   * @param {number} id - Certificate/Order ID
   * @param {boolean} autoRenew - Desired state
   */
  async toggleAutoRenew(id, autoRenew) {
    try {
      const response = await apiClient.post('/api/user/ssl/certificates/toggle-auto-renew', { id, auto_renew: autoRenew });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Renew certificate
   * @param {number} orderId - Order ID to renew
   */
  async renewCertificate(orderId) {
    try {
      const response = await apiClient.post('/api/user/ssl/certificates/renew', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Order Management
  // ==========================================

  /**
   * Get SSL orders
   * @param {number} page - Page number
   * @param {number} perPage - Items per page
   */
  async getOrders(page = 1, perPage = 10) {
    try {
      const response = await apiClient.get('/api/user/ssl/orders', {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single SSL order
   * @param {number} id - Order ID
   */
  async getOrder(id) {
    try {
      const response = await apiClient.get('/api/user/ssl/orders/get', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create SSL order
   * @param {object} data - Order data (product_id, domain, etc.)
   */
  async createOrder(data) {
    try {
      const response = await apiClient.post('/api/user/ssl/orders/create', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Submit domain validation
   * @param {number} orderId - Order ID
   * @param {string} validationMethod - Validation method (dns, http, email)
   */
  async submitValidation(orderId, validationMethod) {
    try {
      const response = await apiClient.post('/api/user/ssl/orders/validate', {
        order_id: orderId,
        validation_method: validationMethod
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Download certificate
   * @param {number} orderId - Order ID
   */
  async downloadCertificate(orderId) {
    try {
      const response = await apiClient.post('/api/user/ssl/orders/download', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Refresh order status from the CA provider
   * @param {number} orderId - Order ID
   */
  async refreshOrderStatus(orderId) {
    try {
      const response = await apiClient.post('/api/user/ssl/orders/refresh-status', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get validation status for an order (domain/organization verification progress)
   * @param {number} orderId - Order ID
   */
  async getValidationStatus(orderId) {
    try {
      const response = await apiClient.get('/api/user/ssl/orders/validation-status', {
        params: { order_id: orderId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Change domain validation method for a pending order
   * @param {number} orderId - Order ID
   * @param {string} method - New validation method (dns, http, email, ...)
   * @param {string} domain - Optional domain (for provider re-issue of tokens)
   */
  async changeValidationMethod(orderId, method, domain = null) {
    try {
      const payload = { order_id: orderId, method };
      if (domain) payload.domain = domain;
      const response = await apiClient.post('/api/user/ssl/orders/change-validation-method', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Resend validation/order notifications for an order
   * @param {number} orderId - Order ID
   */
  async resendNotifications(orderId) {
    try {
      const response = await apiClient.post('/api/user/ssl/orders/resend-notifications', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Providers & Products (Read-Only)
  // ==========================================

  /**
   * Get SSL providers
   */
  async getProviders() {
    try {
      const response = await apiClient.get('/api/user/ssl/providers');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get SSL products
   * @param {string} category - Filter by category (ssl, code_signing)
   */
  async getProducts(category = null) {
    try {
      const params = {};
      if (category) {
        params.category = category;
      }
      const response = await apiClient.get('/api/user/ssl/products', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get Code Signing products
   */
  async getCodeSigningProducts() {
    try {
      const response = await apiClient.get('/api/user/ssl/products', {
        params: { category: 'code_signing' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get providers that support code signing
   */
  async getCodeSigningProviders() {
    try {
      const response = await apiClient.get('/api/user/ssl/providers', {
        params: { supports_code_signing: true }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create Code Signing order
   * @param {object} data - Order data
   */
  async createCodeSigningOrder(data) {
    try {
      // Dedicated code-signing order endpoint (postCodeSigningOrder). The
      // generic /orders/create endpoint only configures an already-pending
      // order, so code-signing orders must use this route.
      const response = await apiClient.post('/api/user/ssl/orders/codesigning', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single product detail (SSL or code signing)
   * @param {number} id - Product ID
   */
  async getProductDetail(id) {
    try {
      const response = await apiClient.get('/api/user/ssl/products/detail', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Lifecycle (2026-10-07)
  // ==========================================
  async _post(url, data) {
    try {
      const response = await apiClient.post(url, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async _get(url, params) {
    try {
      const response = await apiClient.get(url, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  downloadFormat(orderId, format, password = '') {
    return this._post('/api/user/ssl/orders/download', { order_id: orderId, format, password });
  }

  getApproverEmails(orderId, domain) {
    return this._get('/api/user/ssl/orders/approver-emails', { order_id: orderId, domain });
  }

  getDeployTargets(orderId) {
    return this._get('/api/user/ssl/orders/deploy-targets', { order_id: orderId });
  }

  deploy(orderId, target, targetId) {
    return this._post('/api/user/ssl/orders/deploy', { order_id: orderId, target, target_id: targetId });
  }

  reissue(orderId, data = {}) {
    return this._post('/api/user/ssl/orders/reissue', { order_id: orderId, ...data });
  }

  revoke(orderId, reason = 'unspecified') {
    return this._post('/api/user/ssl/orders/revoke', { order_id: orderId, reason });
  }

  // paid SAN slots for an issued multi-domain certificate → add-on invoice(s)
  buySan(orderId, single = 0, wildcard = 0) {
    return this._post('/api/user/ssl/orders/buy-san', { order_id: orderId, single, wildcard });
  }

  // ACME subscription (Sectigo CaaS)
  getAcme(orderId) {
    return this._get('/api/user/ssl/acme', { order_id: orderId });
  }

  acmeRefresh(orderId) {
    return this._post('/api/user/ssl/acme/refresh', { order_id: orderId });
  }

  acmeAddDomains(orderId, domains) {
    return this._post('/api/user/ssl/acme/domains/add', { order_id: orderId, domains });
  }

  acmeRemoveDomain(orderId, domain) {
    return this._post('/api/user/ssl/acme/domains/remove', { order_id: orderId, domain });
  }

  acmeBuySlots(orderId, single = 0, wildcard = 0) {
    return this._post('/api/user/ssl/acme/slots', { order_id: orderId, single, wildcard });
  }

  acmeStopAutoRenew(orderId) {
    return this._post('/api/user/ssl/acme/stop-auto-renew', { order_id: orderId });
  }

  acmeResumeAutoRenew(orderId) {
    return this._post('/api/user/ssl/acme/resume-auto-renew', { order_id: orderId });
  }

  acmeCancel(orderId, reason = '') {
    return this._post('/api/user/ssl/acme/cancel', { order_id: orderId, reason });
  }
}

export default new SSLService();
